import { FC } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineDuplicate } from "react-icons/hi";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";

export interface CopyButtonProps {
  /** The text that gets written to the clipboard when clicked. */
  value: string;
  /** How long the "copied" tooltip stays visible, in milliseconds. Defaults to 1000. */
  resetDelayMs?: number;
}

/**
 * Icon copy button with a transient "copied" tooltip. Reproduces the inline
 * copy affordance used in the order detail panels (duplicate icon + tooltip).
 */
export const CopyButton: FC<CopyButtonProps> = ({ value, resetDelayMs }) => {
  const { t } = useTranslation(["common"]);
  const { copied, copy } = useCopyToClipboard(resetDelayMs);

  const handleClick = () => copy(value);

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        className="text-muted hover:text-lgb-blue-600 dark:hover:text-brand p-1.5 rounded-md hover:bg-brand-subtle dark:hover:bg-lgb-blue-900/20 transition-all duration-200 hover:scale-110 active:scale-95"
      >
        <HiOutlineDuplicate
          className={`w-5 h-5 transform transition-transform duration-200 ${copied ? "scale-90" : ""}`}
        />
      </button>
      <div
        className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-sm transition-opacity duration-200 ${copied ? "opacity-100" : "opacity-0"} pointer-events-none`}
      >
        {t("common:copied")}
      </div>
    </div>
  );
};
